import { useTranslation } from "react-i18next";

import { Stack } from "@mui/material";

import { ClientContent } from "../../../client-content/ClientContent";
import { useClientFileContext } from "../../../context/ClientFileContext";
import { ChecklistQuestion } from "../checklist-question/ChecklistQuestion";
import { ChecklistSectionCard } from "../section-card/ChecklistSectionCard";
import { getQuestionStates } from "./DangerousDebts.utils";
import { useDangerousDebts } from "./hooks/useDangerousDebts";

const TRANSLATION_BASE = "clients.client.debtSettlement.bankruptcyChecklist";

export function DangerousDebts() {
  const { t } = useTranslation();
  const { clientId } = useClientFileContext();
  const { formik, setAnswer } = useDangerousDebts(clientId);

  const states = getQuestionStates(formik.values);

  return (
    <ClientContent formik={formik}>
      <Stack gap={2}>
        <ChecklistSectionCard title={t(`${TRANSLATION_BASE}.sections.wohnungskosten`)}>
          <ChecklistQuestion
            question={t(`${TRANSLATION_BASE}.questions.mietrueckstand`)}
            value={formik.values.hasRentArrears}
            onChange={(value) => setAnswer("hasRentArrears", value)}
            warning={{
              active: states.hasRentArrears.isWarning,
              text: t(`${TRANSLATION_BASE}.warnings.mietrueckstand`),
            }}
            info={t(`${TRANSLATION_BASE}.info.mietrueckstand`)}
          />
          <ChecklistQuestion
            question={t(`${TRANSLATION_BASE}.questions.betriebskosten`)}
            value={formik.values.hasUtilityArrears}
            onChange={(value) => setAnswer("hasUtilityArrears", value)}
            warning={{
              active: states.hasUtilityArrears.isWarning,
              text: t(`${TRANSLATION_BASE}.warnings.betriebskosten`),
            }}
            info={t(`${TRANSLATION_BASE}.info.betriebskosten`)}
          />
        </ChecklistSectionCard>

        <ChecklistSectionCard title={t(`${TRANSLATION_BASE}.sections.strafen`)}>
          <ChecklistQuestion
            question={t(`${TRANSLATION_BASE}.questions.offeneStrafen`)}
            value={formik.values.hasOpenPenalties}
            onChange={(value) => setAnswer("hasOpenPenalties", value)}
            conditionalInfo={
              formik.values.hasOpenPenalties === true ? t(`${TRANSLATION_BASE}.info.strafen`) : undefined
            }
          />
          {states.hasInstallmentAgreement.visible && (
            <ChecklistQuestion
              question={t(`${TRANSLATION_BASE}.questions.ratenvereinbarung`)}
              value={formik.values.hasInstallmentAgreement}
              onChange={(value) => setAnswer("hasInstallmentAgreement", value)}
              warning={{
                active: states.hasInstallmentAgreement.isWarning,
                text: t(`${TRANSLATION_BASE}.warnings.strafen`),
              }}
            />
          )}
          {states.canMeetInstallmentAgreement.visible && (
            <ChecklistQuestion
              question={t(`${TRANSLATION_BASE}.questions.einhaltung`)}
              value={formik.values.canMeetInstallmentAgreement}
              onChange={(value) => setAnswer("canMeetInstallmentAgreement", value)}
              warning={{
                active: states.canMeetInstallmentAgreement.isWarning,
                text: t(`${TRANSLATION_BASE}.warnings.strafen`),
              }}
            />
          )}
        </ChecklistSectionCard>

        <ChecklistSectionCard title={t(`${TRANSLATION_BASE}.sections.alimente`)}>
          <ChecklistQuestion
            question={t(`${TRANSLATION_BASE}.questions.kinderMitUnterhalt`)}
            value={formik.values.hasChildrenWithMaintenance}
            onChange={(value) => setAnswer("hasChildrenWithMaintenance", value)}
          />
          {states.paysFullMaintenance.visible && (
            <ChecklistQuestion
              question={t(`${TRANSLATION_BASE}.questions.vollenUnterhalt`)}
              value={formik.values.paysFullMaintenance}
              onChange={(value) => setAnswer("paysFullMaintenance", value)}
            />
          )}
          {states.isMaintenanceGarnished.visible && (
            <ChecklistQuestion
              question={t(`${TRANSLATION_BASE}.questions.wirdGepfaendet`)}
              value={formik.values.isMaintenanceGarnished}
              onChange={(value) => setAnswer("isMaintenanceGarnished", value)}
              warning={{
                active: states.isMaintenanceGarnished.isWarning,
                text: t(`${TRANSLATION_BASE}.warnings.aliment`),
              }}
            />
          )}
        </ChecklistSectionCard>
      </Stack>
    </ClientContent>
  );
}
